import React, { useState, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import styles from './Action.module.css';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  account_details_url,
  transaction_logs_url,
  submit_action_url,
  response_logs_url,
} from '../../constants/apiEndpoints';

const MAX_LENGTH = 1000;
const MAX_TOTAL_FILE_SIZE = 800 * 1024;

const Action = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const acknowledge_no = location.state?.acknowledge_no || '';
  const job_id = location.state?.job_id || '';

  const [selectedFileName, setSelectedFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [fileError, setFileError] = useState('');
  const [remarksError, setRemarksError] = useState('');
  const toast = useRef(null);
  const fileInputRef = useRef(null);

  const clearFormFields = () => {
    setRemarks('');
    setRemarksError('');
    setSelectedFileName('');
    setSelectedFile(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const { data: accountData, isLoading: isLoadingAccount } = useQuery({
    queryKey: ['actionDetails', acknowledge_no],
    queryFn: () =>
      axios
        .get(`${account_details_url}?acknowledgement_no=${acknowledge_no}`)
        .then((res) => res.data)
        .catch(error=>{
          console.log("Something went wrong",error);
          
        }),
    enabled: !!acknowledge_no,
  });

  const { data: logsData, isLoading: isLoadingLogs } = useQuery({
    queryKey: ['transactionLogs', acknowledge_no],
    queryFn: () =>
      axios
        .get(`${transaction_logs_url}?acknowledgement_id=${acknowledge_no}`)
        .then((res) => res.data)
        .catch(error=>{
          console.log("Something went wrong",error);
          
        }),
    enabled: !!acknowledge_no,
  });

  const { data: responseLogsData, isLoading: isLoadingResponseLogs } = useQuery({
    queryKey: ['responseLogs', acknowledge_no],
    queryFn: () =>
      axios
        .get(`${response_logs_url}?acknowledgement_no=${acknowledge_no}`)
        .then((res) => res.data)
        .catch(error=>{
          console.log("Something went wrong",error);
          
        }),
    enabled: !!acknowledge_no,
  });

  const beneficiaryDetails = accountData?.payee_account_details?.[0] || {};
  const victimDetails = accountData?.victim_account_details || {};
  const victimTransactions = victimDetails ? [victimDetails] : [];

  const responseLogDetails = responseLogsData?.message?.[0];
  // const responseLogTransactions = responseLogDetails?.transaction_details?.[0]?.message ? [] : responseLogDetails?.transaction_details
  const responseLogTransactions = responseLogDetails?.transaction_details?.[0]?.message || responseLogDetails?.transaction_details?.[0]?.Failed
  ? []
  : responseLogDetails?.transaction_details;

  const logsList = logsData?.message?.[0]?.transaction_logs || [];
  const logsText = logsList.length ? logsList.join('\n') : '';

  const { mutate, isLoading: isSubmitting } = useMutation({
    mutationFn: async ({ job_id, remarks, attachment }) => {
      const formData = new FormData();
      formData.append('job_id', job_id);
      formData.append('remarks', remarks);
      if (attachment) {
        formData.append('attachment', attachment);
      }
      const res = await axios.post(submit_action_url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (data) => {
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: data.message,
        life: 3000,
      });
      // clearFormFields();
    },
    onError: (error) => {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'An error occurred',
        life: 3000,
      });
    },
  });

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > MAX_TOTAL_FILE_SIZE) {
        setFileError(
          `File size should not exceed ${(MAX_TOTAL_FILE_SIZE / 1024).toFixed(0)} KB`
        );
        setSelectedFileName('');
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      setFileError('');
      setSelectedFileName(file.name);
      setSelectedFile(file);
    } else {
      setSelectedFileName('');
      setSelectedFile(null);
      setFileError('');
    }
  };

  const handleOk = () => {
    let hasError = false;
    if (!remarks.trim()) {
      setRemarksError('Remarks are required');
      hasError = true;
    } else {
      setRemarksError('');
    }
    if (!selectedFile) {
      setFileError('Please attach the file');
      hasError = true;
    } else {
      setFileError('');
    }
    if (fileError) hasError = true;
    if (hasError) return;
    mutate({ job_id, remarks, attachment: selectedFile });
  };

  const severityColor = (status)=>{
    const compareStatus = status?.toLowerCase();
    
    if(compareStatus === 'failed'){
      return 'danger'
    }
    else if(compareStatus === 'open'){
      return 'warning'
    }
    else if(compareStatus === 'closed'){
      return 'success'
    }
    else if(compareStatus === 'active'){
      return 'primary'
    }
    else{
      return 'primary'
    }
  }

  const handleCancel = () => {
    navigate('/cases');
  };

  const renderLoadingState = () => (
    <div style={{ textAlign: 'center', padding: '2rem' }}>Loading details...</div>
  );



  return (
    <div className={styles.container}>
      <Toast ref={toast} />

      <div className={styles.caseHeader}>
        <span className={styles.caseRef}>
          Case Reference
          <span className={styles.refNum}>{acknowledge_no || 'N/A'}</span>
        </span>
      </div>

      {isLoadingAccount ? (
        renderLoadingState()
      ) : (
        <>
          {/* Beneficiary Bank Details */}
          <div>
            <h3 style={{ color: '#0096CC' }}>Payee Bank Details</h3>
            <div className={styles.topInfo}>
              <div className={styles.leftSection}>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Name</b></span>
                  <span className={styles.value}>{beneficiaryDetails?.payee_full_name || 'N/A'}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Account No</b></span>
                  <span className={styles.value}>{beneficiaryDetails?.payee_account_number || 'N/A'}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Branch</b></span>
                  <span className={styles.value}>{beneficiaryDetails?.payee_branch_name || 'N/A'}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Available Balance</b></span>
                  <span className={styles.value}>
                    {victimDetails.payee_account_balance ? `₹ ${victimDetails?.payee_account_balance}` : 'N/A'}
                  </span>
                </div>
              </div>
              <div className={styles.centerSection}>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Disputed Amount</b></span>
                  <span className={styles.value}>
                    {victimDetails.disputed_amount ? `₹ ${victimDetails?.disputed_amount}` : 'N/A'}
                  </span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Payment Mode</b></span>
                  <span className={styles.value}>{victimDetails?.payment_mode || 'N/A'}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Payee Email</b></span>
                  <span className={styles.value}>{beneficiaryDetails?.payee_email || 'N/A'}</span>
                </div>
              </div>
              <div className={styles.rightSection}>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Bank Name</b></span>
                  <span className={styles.value}>{beneficiaryDetails?.payee_bank || 'N/A'}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Bank Code</b></span>
                  <span className={styles.value}>{beneficiaryDetails?.payee_bank_code}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Account Status</b></span>
                  <Tag severity={severityColor(victimDetails?.account_status)} value={victimDetails?.account_status?.charAt(0).toUpperCase() + victimDetails?.account_status.slice(1) || 'N/A'} />
                </div>
              </div>
            </div>
          </div>

          {/* Victim's Bank Details */}
          <div style={{ marginTop: '1rem' }}>
            <h3 style={{ color: '#0096CC' }}>Payer Bank Details</h3>
            <div className={styles.topInfo}>
              <div className={styles.leftSection}>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Name</b></span>
                  <span className={styles.value}>{victimDetails?.requester || 'N/A'}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Branch</b></span>
                  <span className={styles.value}>{victimDetails?.branch || 'N/A'}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Email</b></span>
                  <span className={styles.value}>{victimDetails?.email_id || 'N/A'}</span>
                </div>
              </div>
              <div className={styles.centerSection}>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Disputed Amount</b></span>
                  <span className={styles.value}>
                    {victimDetails.disputed_amount ? `₹ ${victimDetails?.disputed_amount}` : 'N/A'}
                  </span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Payment Mode</b></span>
                  <span className={styles.value}>{victimDetails?.payment_mode || 'N/A'}</span>
                </div>
              </div>
              <div className={styles.rightSection}>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Bank Name</b></span>
                  <span className={styles.value}>{victimDetails?.payer_bank || 'N/A'}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Bank Code</b></span>
                  <span className={styles.value}>{victimDetails?.payer_bank_code || 'N/A'}</span>
                </div>
                <div className={styles.labelValueRow}>
                  <span className={styles.label}><b>Request Status</b></span>
                  <Tag severity={severityColor(victimDetails?.request_status)}  value={victimDetails?.request_status || 'N/A'} />
                </div>
              </div>
            </div>
          </div>

          {/* Victim Transactions Table */}
          <h3 style={{ color: '#0096CC' }}>Victim’s Transaction Details</h3>
          <div className={styles.dataTableWrap}>
            <DataTable
              value={victimTransactions}
              stripedRows
              size="small"
              showGridlines
              loading={isLoadingAccount}
              scrollable
              emptyMessage="No Data Available"
              scrollHeight="250px"
            >
              <Column field="acknowledgement_no" header="Acknowledgement No" />
              <Column field="disputed_amount" header="Disputed Amount" />
              <Column field="layer" header="Layer" />
              <Column field="rrn_id" header="Rrn" />
              <Column field="transaction_date" header="Transaction Date" />
              <Column field="transaction_time" header="Transaction Time" />
            </DataTable>
          </div>

          {/* Logs, Response & Remarks */}
          <h3 style={{ color: '#0096CC' }}>Response Log Details</h3>
          <div className={styles.textAreas}>
            <div className={styles.textAreaWrapper}>
              {/* <label>Response</label> */}
              <div className={styles.responseLogBox}>
                {isLoadingResponseLogs ? (
                  <div style={{ padding: '1rem', textAlign: 'center' }}>
                    Loading response logs...
                  </div>
                ) : (
                  <div className={styles.scrollableContent}>
                    <div className={styles.responseLogDetails}>
                      <div className={styles.labelValueRow}>
                        <span className={styles.label}><b>Acknowledgement No</b></span>
                        <span className={styles.value}>{responseLogDetails?.acknowledgement_no || 'N/A'}</span>
                      </div>
                      <div className={styles.labelValueRow}>
                        <span className={styles.label}><b>Created Time</b></span>
                        <span className={styles.value}>{responseLogDetails?.created_time || 'N/A'}</span>
                      </div>
                      <div className={styles.labelValueRow}>
                        <span className={styles.label}><b>Disputed Amount</b></span>
                        <span className={styles.value}>{responseLogDetails?.disputed_amount}</span>
                      </div>
                      <div className={styles.labelValueRow}>
                        <span className={styles.label}><b>Hold Amount</b></span>
                        <span className={styles.value}>{responseLogDetails?.hold_amount}</span>
                      </div>
                      <div className={styles.labelValueRow}>
                        <span className={styles.label}><b>Job ID</b></span>
                        <span className={styles.value}>{responseLogDetails?.job_id || 'N/A'}</span>
                      </div>
                    </div>

                    <h4 style={{ color: '#0096CC', margin: '1rem 0 0.5rem 0' }}>Response Transaction Details</h4>
                    <div className={styles.tableContainer}>
                      <DataTable
                        value={responseLogTransactions}
                        stripedRows
                        size="small"
                        showGridlines
                        scrollable
                        emptyMessage="No Data Available"
                        scrollHeight="200px"
                      >
                        <Column field="amount" header="Amount" style={{ minWidth: '100px' }} />
                        <Column field="description" header="Description" style={{ minWidth: '200px' }} />
                        <Column field="txn_type" header="Txn Type" style={{ minWidth: '120px' }} />
                        <Column field="ifsc_code" header="IFSC Code" style={{ minWidth: '150px' }} />
                        <Column field="pan_number" header="PAN Number" style={{ minWidth: '150px' }} />
                        <Column field="phone_number" header="Phone Number" style={{ minWidth: '150px' }} />
                        <Column field="root_account_number" header="Root Account No" style={{ minWidth: '150px' }} />
                        <Column field="root_bank_id" header="Root Bank ID" style={{ minWidth: '150px' }} />
                        <Column field="root_effective_balance" header="Root Effective Balance" style={{ minWidth: '180px' }} />
                        <Column field="root_ifsc_code" header="Root IFSC Code" style={{ minWidth: '150px' }} />
                        <Column field="root_rrn_transaction_id" header="Root RRN Transaction ID" style={{ minWidth: '200px' }} />
                        <Column field="status_code" header="Status Code" style={{ minWidth: '120px' }} />
                        <Column field="transaction_datetime" header="Transaction DateTime" style={{ minWidth: '220px' }} />
                        <Column field="email" header="Email" style={{ minWidth: '200px' }} />
                        <Column field="remarks" header="Remarks" style={{ minWidth: '200px' }} />

                      </DataTable>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.textAreaWrapper}>
              <label>Logs</label>
              <div className={styles.textareaWithCounter}>
                <InputTextarea
                  rows={8}
                  value={isLoadingLogs ? 'Loading...' : logsText}
                  readOnly
                  placeholder="Your logs will be visible here..."
                  className={styles.inputArea}
                />
              </div>
            </div>

            <div className={styles.textAreaWrapper}>
              <label>Remarks</label>
              <div className={styles.textareaWithCounter}>
                <InputTextarea
                  rows={3}
                  maxLength={MAX_LENGTH}
                  className={styles.inputArea}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter your remarks..."
                />
                <div className={styles.charCounter}>{`${remarks.length}/${MAX_LENGTH}`}</div>
                {remarksError && <small className={styles.errorMessage}>{remarksError}</small>}
              </div>
            </div>
          </div>

          <div className={styles.attachmentsSection}>
            <div className={styles.attachmentsRow}>
              <label htmlFor="fileUpload" className={styles.attachmentsLabel}>Attachment</label>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <InputText
                  id="selectedFile"
                  value={selectedFileName}
                  readOnly
                  placeholder="No file selected"
                  className={styles.attachmentsInput}
                />
                {fileError && <small className={styles.errorMessage}>{fileError}</small>}
              </div>
              <input
                type="file"
                id="fileUpload"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                label="Browse"
                style={{ backgroundColor: '#0096CC' }}
                className={styles.buttonSize}
                onClick={() => fileInputRef.current.click()}
              />
            </div>
          </div>

          <div className={styles.footer}>
            <Button
              label="Cancel"
              style={{ backgroundColor: '#0096CC' }}
              className={styles.buttonSize}
              onClick={handleCancel}
            />
            <Button
              label={isSubmitting ? 'Submitting...' : 'OK'}
              style={{ backgroundColor: '#0096CC' }}
              className={styles.buttonSize}
              onClick={handleOk}
              disabled={isSubmitting}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Action;
